/**
 * Utilidades de exportación para el módulo IR-13
 * Declaración Jurada Anual de Retenciones ISR - DGII República Dominicana
 */

/**
 * Formatea un número con 2 decimales, sin separadores de miles.
 * Usa punto como separador decimal (formato requerido por DGII).
 * @param {number|string} value
 * @returns {string}
 */
const fmt = (value) => {
  const num = parseFloat(value) || 0;
  return num.toFixed(2);
};

/**
 * Escapa un campo para CSV: envuelve en comillas dobles si contiene coma,
 * comilla doble o salto de línea, escapando las comillas internas.
 * @param {string|number} value
 * @returns {string}
 */
const csvField = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Exporta una declaración IR-13 a CSV con formato DGII.
 *
 * Estructura del archivo:
 *   Fila 1: Título de la declaración con el año
 *   Fila 2: Vacía
 *   Fila 3: Cabeceras de columnas en MAYÚSCULAS
 *   Filas 4..N: Una fila por empleado
 *   Última fila: Totales
 *
 * @param {Object} ir13Header - Objeto cabecera de la declaración (debe contener { anio })
 * @param {Array}  detalles   - Arreglo de filas de empleados
 *
 * Campos esperados en cada elemento de `detalles`:
 *   apellidos_nombres | nombres_apellidos | nombre_completo  — nombre del empleado
 *   rnc_cedula | cedula | rnc                               — documento de identidad
 *   sueldos_pagados                                         — sueldos y salarios
 *   otras_remuneraciones                                    — bonificaciones, etc.
 *   total_pagado                                            — suma total bruta
 *   retencion_seguridad_social | retencion_ss               — cuota del empleado SS
 *   isr_retenido                                            — ISR descontado en nómina
 *   isr_liquidado                                           — ISR determinado anual
 *   saldo_favor | saldo_a_favor                             — saldo a favor del empleado
 *   monto_pagar | monto_a_pagar                             — saldo a pagar al fisco
 */
export function exportIR13CSV(ir13Header, detalles) {
  const anio = ir13Header?.anio || new Date().getFullYear();

  // ── Columnas DGII (orden exacto) ────────────────────────────────────────────
  const HEADERS = [
    'APELLIDOS Y NOMBRES',
    'RNC/CÉDULA',
    'SUELDOS PAGADOS',
    'OTRAS REMUNERACIONES',
    'TOTAL PAGADO',
    'RETENCIÓN SEGURIDAD SOCIAL',
    'ISR RETENIDO',
    'ISR LIQUIDADO',
    'SALDO A FAVOR',
    'MONTO A PAGAR',
  ];

  // ── Totales acumuladores ────────────────────────────────────────────────────
  let totSueldos = 0;
  let totOtras = 0;
  let totTotal = 0;
  let totSS = 0;
  let totISRRet = 0;
  let totISRLiq = 0;
  let totSaldo = 0;
  let totMonto = 0;

  // ── Filas de datos ──────────────────────────────────────────────────────────
  const dataRows = (detalles || []).map((d) => {
    const nombre =
      d.apellidos_nombres ||
      d.nombres_apellidos ||
      d.nombre_completo ||
      '';
    const cedula =
      d.rnc_cedula || d.cedula || d.rnc || '';
    const sueldos         = parseFloat(d.sueldos_pagados)                           || 0;
    const otras           = parseFloat(d.otras_remuneraciones)                      || 0;
    const total           = parseFloat(d.total_pagado)                              || 0;
    const ss              = parseFloat(d.retencion_seguridad_social ?? d.retencion_ss) || 0;
    const isrRet          = parseFloat(d.isr_retenido)                              || 0;
    const isrLiq          = parseFloat(d.isr_liquidado)                             || 0;
    const saldo           = parseFloat(d.saldo_favor ?? d.saldo_a_favor)            || 0;
    const monto           = parseFloat(d.monto_pagar ?? d.monto_a_pagar)            || 0;

    totSueldos += sueldos;
    totOtras   += otras;
    totTotal   += total;
    totSS      += ss;
    totISRRet  += isrRet;
    totISRLiq  += isrLiq;
    totSaldo   += saldo;
    totMonto   += monto;

    return [
      csvField(nombre),
      csvField(cedula),
      fmt(sueldos),
      fmt(otras),
      fmt(total),
      fmt(ss),
      fmt(isrRet),
      fmt(isrLiq),
      fmt(saldo),
      fmt(monto),
    ].join(',');
  });

  // ── Fila de totales ─────────────────────────────────────────────────────────
  const totalsRow = [
    'TOTALES',
    '',
    fmt(totSueldos),
    fmt(totOtras),
    fmt(totTotal),
    fmt(totSS),
    fmt(totISRRet),
    fmt(totISRLiq),
    fmt(totSaldo),
    fmt(totMonto),
  ].join(',');

  // ── Construcción del contenido CSV ──────────────────────────────────────────
  const title = `IR-13 - DECLARACIÓN JURADA ANUAL DE RETENCIONES ISR - AÑO ${anio}`;
  const lines = [
    title,
    '',
    HEADERS.join(','),
    ...dataRows,
    totalsRow,
  ];

  const csv = '\uFEFF' + lines.join('\n');

  // ── Descarga ────────────────────────────────────────────────────────────────
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `IR13_${anio}_${today}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Exporta una declaración IR-13 a TXT con separador de tubo (pipe) — formato DGII.
 *
 * Cada fila tiene el siguiente orden de campos:
 *   CEDULA|APELLIDOS_NOMBRES|SUELDOS_PAGADOS|OTRAS_REMUNERACIONES|TOTAL_PAGADO|
 *   RETENCION_SS|ISR_RETENIDO|ISR_LIQUIDADO|SALDO_FAVOR|MONTO_PAGAR
 *
 * Números con 2 decimales, sin separadores de miles, punto como decimal.
 *
 * @param {Object} ir13Header - Objeto cabecera de la declaración (debe contener { anio })
 * @param {Array}  detalles   - Arreglo de filas de empleados (mismos campos que exportIR13CSV)
 */
export function exportIR13TXT(ir13Header, detalles) {
  const anio = ir13Header?.anio || new Date().getFullYear();

  const lines = (detalles || []).map((d) => {
    const cedula  = d.rnc_cedula || d.cedula || d.rnc || '';
    const nombre  =
      d.apellidos_nombres ||
      d.nombres_apellidos ||
      d.nombre_completo ||
      '';
    const sueldos = parseFloat(d.sueldos_pagados)                               || 0;
    const otras   = parseFloat(d.otras_remuneraciones)                          || 0;
    const total   = parseFloat(d.total_pagado)                                  || 0;
    const ss      = parseFloat(d.retencion_seguridad_social ?? d.retencion_ss)  || 0;
    const isrRet  = parseFloat(d.isr_retenido)                                  || 0;
    const isrLiq  = parseFloat(d.isr_liquidado)                                 || 0;
    const saldo   = parseFloat(d.saldo_favor ?? d.saldo_a_favor)                || 0;
    const monto   = parseFloat(d.monto_pagar ?? d.monto_a_pagar)                || 0;

    return [
      cedula,
      nombre,
      fmt(sueldos),
      fmt(otras),
      fmt(total),
      fmt(ss),
      fmt(isrRet),
      fmt(isrLiq),
      fmt(saldo),
      fmt(monto),
    ].join('|');
  });

  const content = lines.join('\n');

  // ── Descarga ────────────────────────────────────────────────────────────────
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `IR13_${anio}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}
